import { formatTimeForDisplay, getTimezoneDisplayName } from '../lib/formatUtils';

// Unit utility functions for handling metric/imperial conversions
export const getUnitFromSettings = (): string => {
  try {
    const settings = localStorage.getItem('settings');
    if (settings) {
      const settingsData = JSON.parse(settings);
      const unit = settingsData.general?.unit || 'Imperial';
      // Convert from settings format to internal format
      return unit === 'Metric' ? 'mm' : 'in';
    }
  } catch (error) {
  }
  return 'in'; // Default fallback
};

export const getUnitLabel = (unit: string): string => {
  switch (unit) {
    case 'mm':
      return 'mm';
    case 'Inches':
      return 'in';
    default:
      return 'in';
  }
};

export const getUnitDisplayText = (unit: string): string => {
  switch (unit) {
    case 'mm':
      return 'Metric (mm)';
    case 'Inches':
      return 'Imperial (in)';
    default:
      return 'Metric (mm)';
  }
};

// Helper function to round to 4 decimal places
export const roundToTwoDecimalPlaces = (value: number): number => {
  return Math.round(value * 10000) / 10000;
};

/** Round to 4 decimal places for unit conversion */
const roundTo4Decimals = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  return Math.round(value * 10000) / 10000;
};

/**
 * Convert value from user's unit to inches (for database save).
 * DB always stores in inches.
 */
export const convertValueToInches = (value: number, unit: string): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  const v = Number(value);
  switch (unit) {
    case 'in': return roundTo4Decimals(v);
    case 'cm': return roundTo4Decimals(v / 2.54);
    case 'mm': return roundTo4Decimals(v / 25.4);
    case 'ft': return roundTo4Decimals(v * 12);
    case 'm': return roundTo4Decimals(v * 39.3701);
    default: return roundTo4Decimals(v);
  }
};

/**
 * Convert value from inches (database) to user's display unit.
 */
export const convertValueFromInches = (value: number, unit: string): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  const v = Number(value);
  switch (unit) {
    case 'in': return roundTo4Decimals(v);
    case 'cm': return roundTo4Decimals(v * 2.54);
    case 'mm': return roundTo4Decimals(v * 25.4);
    case 'ft': return roundTo4Decimals(v / 12);
    case 'm': return roundTo4Decimals(v / 39.3701);
    default: return roundTo4Decimals(v);
  }
};

// Convert values between units if needed
export const convertValue = (value: number, fromUnit: string, toUnit: string): number => {
  if (fromUnit === toUnit) return value;
  
  // Convert mm to inches
  if (fromUnit === 'mm' && toUnit === 'Inches') {
    return value / 25.4;
  }
  
  // Convert inches to mm
  if (fromUnit === 'Inches' && toUnit === 'mm') {
    return value * 25.4;
  }
  
  return roundToTwoDecimalPlaces(value);  //round to 2 decimal places for display
};

// Convert weld data value from inches (API base) to display unit based on settings
export const convertWeldDataValue = (value: number, parameter?: string): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from inches to mm
  if (displayUnit === 'mm') {
    // For energy parameters (heat, trueEnergy), convert from kJ/in to kJ/mm
    if (parameter === 'heat' || parameter === 'trueEnergy') {
      return value / 25.4; // Convert kJ/in to kJ/mm
    }
    // For physical measurements (travelSpeed, wireSpeed, totalWireConsumed, etc.), convert from inches to mm
    if (parameter === 'travelSpeed' || parameter === 'wireSpeed' || parameter === 'totalWireConsumed' || parameter === 'oscillationWidth' || parameter === 'distance') {
      return value * 25.4;
    }
    // For other parameters, convert from inches to mm
    return value * 25.4;
  }
  
  // If display unit is inches, return as is (no conversion needed)
  return roundToTwoDecimalPlaces(value);  //round to 2 decimal places for display
};

// Convert setup value from inches (API base) to display unit based on settings
export const convertSetupValue = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from inches to mm and round to 3 decimal places
  if (displayUnit === 'mm') {
    return Math.round(value * 25.4 * 1000) / 1000;
  }
  
  // If display unit is inches, return same value rounded to 4 decimal places
  return Math.round(value * 10000) / 10000;
};

// Convert setup value back to inches for API calls
export const convertSetupValueToInches = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from mm to inches and round to 4 decimal places
  if (displayUnit === 'mm') {
    return Math.round((value / 25.4) * 10000) / 10000;
  }
  
  // If display unit is inches, return same value rounded to 4 decimal places
  return Math.round(value * 10000) / 10000;
};

// Format weld data value with proper unit label for display
export const formatWeldDataValue = (value: number, parameter?: string): string => {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  
  const displayUnit = getUnitFromSettings();
  const convertedValue = convertWeldDataValue(value, parameter);
  const unitLabel = getUnitLabel(displayUnit);
  
  // Round to 2 decimal places for display
  const roundedValue = Math.round(convertedValue * 100) / 100;
  
  // For specific parameters, you might want different formatting
  if (parameter) {
    switch (parameter) {
      case 'travel_speed':
        return `${roundedValue} ${unitLabel}/min`;
      case 'wire_speed':
        return `${roundedValue} ${unitLabel}/min`;
      case 'oscillation_width':
        return `${roundedValue} ${unitLabel}`;
      case 'total_wire_consumed':
        return `${roundedValue} ${unitLabel}`;
      case 'heat':
      case 'true_energy':
        return `${roundedValue} kJ/${unitLabel}`;
      default:
        return `${roundedValue} ${unitLabel}`;
    }
  }
  
  return `${roundedValue} ${unitLabel}`;
};

// Format setup value with proper unit label for display
export const formatSetupValue = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  
  const displayUnit = getUnitFromSettings();
  const convertedValue = convertSetupValue(value);
  const unitLabel = getUnitLabel(displayUnit);
  
  // Round to 2 decimal places for display
  const roundedValue = Math.round(convertedValue * 100) / 100;
  
  return `${roundedValue} ${unitLabel}`;
};

// Convert weld data object with all values from inches to display units
export const convertWeldDataObject = (weldData: any): any => {
  if (!weldData || typeof weldData !== 'object') return weldData;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is inches, no conversion needed
  if (displayUnit === 'Inches') {
    return weldData;
  }
  
  // Parameters that need conversion from inches to mm
  const convertibleParameters = [
    'travel_speed',
    'wire_speed', 
    'oscillation_width',
    'total_wire_consumed',
    'heat',
    'true_energy'
  ];
  
  const convertedData = { ...weldData };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertWeldDataValue(convertedData[param], param);
    }
  });
  
  return convertedData;
};

// Convert setup pipe object with all values from inches to display units
export const convertSetupPipeObject = (pipeData: any): any => {
  if (!pipeData || typeof pipeData !== 'object') return pipeData;
  
  const displayUnit = getUnitFromSettings();
  const convertfeetToInchesParamerts = [
    'pipeLength',
    'outputAssemblyLength'
  ]
  // If display unit is inches, return same object (no conversion needed)
  if (displayUnit === 'Inches') {
    convertfeetToInchesParamerts.forEach(param => {
      if (pipeData[param] !== undefined && typeof pipeData[param] === 'number') {
        pipeData[param] = convertInchesToFeet(pipeData[param]);
      }
    });
    return pipeData;
  }
  
  // Parameters that need conversion from inches to mm
  const convertibleParameters = [
    'pipeSize',
    'wallThickness',
   
  ];
  
  const convertedData = { ...pipeData };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertSetupValue(convertedData[param]);
    }
  });
  
  return convertedData;
};

// Convert setup pipe object back to inches for API calls
export const convertSetupPipeObjectToInches = (pipeData: any): any => {
  if (!pipeData || typeof pipeData !== 'object') return pipeData;
  
  const displayUnit = getUnitFromSettings();
  const convertfeetToInchesParamerts = [
    'pipeLength',
    'outputAssemblyLength'
  ]
  // If display unit is inches, return same object (no conversion needed)
  if (displayUnit === 'Inches') {
    convertfeetToInchesParamerts.forEach(param => {
      if (pipeData[param] !== undefined && typeof pipeData[param] === 'number') {
        pipeData[param] = convertFeetToInches(pipeData[param]);
      }
    });
    return pipeData;
  }
  
  // Parameters that need conversion from mm to inches
  const convertibleParameters = [
    'pipeSize',
    'wallThickness',
  
  ];
  

  const convertedData = { ...pipeData };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertSetupValueToInches(convertedData[param]);
    }  });
   
  return convertedData;
};

export const convertFeetToInches = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  return roundToTwoDecimalPlaces(value * 12);
};
export const convertInchesToFeet = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  return roundToTwoDecimalPlaces(value / 12);
};

/** Default pipe unit object when not saved */
const DEFAULT_PIPE_UNIT = { pipeSize: 'in', wallThickness: 'in', length: 'in', outputAssemblyLength: 'ft' };

/**
 * Convert pipe values from user's display units to inches for database save.
 * DB always stores pipeSize, wallThickness, pipeLength, outputAssemblyLength in inches.
 * API expects `units` (not `unit`).
 */
export const convertSetupPipeToInchesForSave = (pipe: any): any => {
  if (!pipe || typeof pipe !== 'object') return pipe;
  const u = pipe.unit || pipe.units || DEFAULT_PIPE_UNIT;
  const { unit, ...rest } = pipe;
  return {
    ...rest,
    units: u,
    pipeSize: convertValueToInches(Number(pipe.pipeSize) || 0, u.pipeSize || 'in'),
    wallThickness: convertValueToInches(Number(pipe.wallThickness) || 0, u.wallThickness || 'in'),
    pipeLength: convertValueToInches(Number(pipe.pipeLength) || 0, u.length || 'in'),
    outputAssemblyLength: convertValueToInches(Number(pipe.outputAssemblyLength) || 0, u.outputAssemblyLength || 'ft'),
  };
};

/**
 * Convert pipe values from inches (database) to user's saved display units.
 * API returns `units`; we support both `unit` and `units` for compatibility.
 */
export const convertSetupPipeFromInchesForDisplay = (pipe: any): any => {
  if (!pipe || typeof pipe !== 'object') return pipe;
  const u = pipe.unit || pipe.units || DEFAULT_PIPE_UNIT;
  return {
    ...pipe,
    pipeSize: convertValueFromInches(Number(pipe.pipeSize) || 0, u.pipeSize || 'in'),
    wallThickness: convertValueFromInches(Number(pipe.wallThickness) || 0, u.wallThickness || 'in'),
    pipeLength: convertValueFromInches(Number(pipe.pipeLength) || 0, u.length || 'in'),
    outputAssemblyLength: convertValueFromInches(Number(pipe.outputAssemblyLength) || 0, u.outputAssemblyLength || 'ft'),
  };
};

// Get unit suffix for specific parameter
export const getParameterUnitSuffix = (parameter: string): string => {
  const displayUnit = getUnitFromSettings();
  const unitLabel = getUnitLabel(displayUnit);
  
  switch (parameter) {
    case 'travel_speed':
    case 'wire_speed':
      return `${unitLabel}/min`;
    case 'volts':
      return 'V';
    case 'current':
    case 'target':
    case 'horizontal_bias':
      return 'A';
    case 'frequency':
      return 'cyc/min';
    case 'oscillation_width':
    case 'total_wire_consumed':
    case 'band_diameter':
    case 'wire_diameter':
    case 'logging_distance':
    case 'pipe_diameter':
      return unitLabel;
    case 'true_energy':
    case 'heat':
      return `kJ/${unitLabel}`;
    case 'arc_impedance':
      return 'ohms';
    default:
      return unitLabel;
  }
};

// Convert fitment readings value from inches (API base) to display unit based on settings
export const convertFitmentReadingsValue = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from inches to mm
  if (displayUnit === 'mm') {
    return value * 25.4;
  }
  
  // If display unit is inches, return same value (no conversion needed)
  return Math.round(value * 100) / 100;
};

// Convert fitment readings value back to inches for API calls
export const convertFitmentReadingsValueToInches = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from mm to inches
  if (displayUnit === 'mm') {
    return value / 25.4;
  }
  
  // If display unit is inches, return same value (no conversion needed)
  return value;
};

// Convert fitment readings object with all values from inches to display units
export const convertFitmentReadingsObject = (fitmentReadings: any): any => {
  if (!fitmentReadings || typeof fitmentReadings !== 'object') return fitmentReadings;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is inches, no conversion needed
  if (displayUnit === 'in') {
    return fitmentReadings;
  }
  
  // Parameters that need conversion from inches to mm
  const convertibleParameters = [
    'wallThicknessMin',
    'wallThicknessMax',
    'landMin',
    'landMax',
    'internalHiLowMin',
    'internalHiLowMax',
    'externalHiLowMin',
    'externalHiLowMax',
    'bevelTopRangeMin',
    'bevelTopRangeMax',
    'wallThicknessTopRangeMin',
    'wallThicknessTopRangeMax',
    'gapToleranceMin',
    'gapToleranceMax'
  ];
  
  const convertedData = { ...fitmentReadings };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertFitmentReadingsValue(convertedData[param]);
    }
  });
  
  return convertedData;
};

// Convert fitment readings object back to inches for API calls
export const convertFitmentReadingsObjectToInches = (fitmentReadings: any): any => {
  if (!fitmentReadings || typeof fitmentReadings !== 'object') return fitmentReadings;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is inches, no conversion needed
  if (displayUnit === 'in') {
    return fitmentReadings;
  }
  
  // Parameters that need conversion from mm to inches
  const convertibleParameters = [
    'wallThicknessMin',
    'wallThicknessMax',
    'landMin',
    'landMax',
    'internalHiLowMin',
    'internalHiLowMax',
    'externalHiLowMin',
    'externalHiLowMax',
    'bevelTopRangeMin',
    'bevelTopRangeMax',
    'wallThicknessTopRangeMin',
    'wallThicknessTopRangeMax',
    'gapToleranceMin',
    'gapToleranceMax'
  ];
  
  const convertedData = { ...fitmentReadings };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertFitmentReadingsValueToInches(convertedData[param]);
    }
  });
  
  return convertedData;
};


// Import timezone functions from formatUtils to avoid duplication
export { 
  getTimezoneDisplayName,
  formatTimeForDisplay as formatTimestampForDisplay 
} from '../lib/formatUtils'; 




export const formatDate = (dateString: string ,isTimezone: boolean = false,isUTC: boolean = false) => {
  const date = new Date(dateString);
  
  if(isTimezone){
    if(isUTC){
      // Show UTC time format
      const day = date.getUTCDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
      const year = date.getUTCFullYear();
      const time = date.toLocaleTimeString('en-US', { 
        hour12: false, 
        timeZone: 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      return `${day}-${month}-${year} ${time} UTC`;
    } else {
      // Show local time format (Eastern Time, Pacific Time, etc.)
      return formatTimeForDisplay(dateString) + ' ' + getTimezoneDisplayName();
    }
  }
  
  // Default date format without time
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Convenience functions for common use cases
export const formatDateWithTime = (dateString: string, isUTC: boolean = false) => {
  return formatDate(dateString, true, isUTC);
};

export const formatDateUTC = (dateString: string) => {
  return formatDate(dateString, true, true);
};

export const formatDateLocal = (dateString: string) => {
  return formatDate(dateString, true, false);
};

/**
 * Time Formatting Examples:
 * 
 * // Basic date only (no time)
 * formatDate('2024-01-15T10:30:00Z') 
 * // Returns: "15-Jan-2024"
 * 
 * // Local time with timezone (Eastern Time, Pacific Time, etc.)
 * formatDateLocal('2024-01-15T10:30:00Z')
 * // Returns: "15-Jan-2024, 5:30 AM Eastern Time (EST)" (if EST)
 * 
 * // UTC time format
 * formatDateUTC('2024-01-15T10:30:00Z')
 * // Returns: "15-Jan-2024 10:30:00 UTC"
 * 
 * // Manual control
 * formatDate('2024-01-15T10:30:00Z', true, false) // Local time
 * formatDate('2024-01-15T10:30:00Z', true, true)  // UTC time
 */

    // Utility function to format response time
   export const formatResponseTime = (ms: number): string => {
      if (ms < 1000) {
          return `${ms}ms`;
      }

      const minutes = Math.floor(ms / 60000);
      const seconds = Math.floor((ms % 60000) / 1000);
      const milliseconds = ms % 1000;

      let result = '';
      if (minutes > 0) {
          result += `${minutes}m `;
      }
      if (seconds > 0) {
          result += `${seconds}s `;
      }
      if (milliseconds > 0) {
          result += `${milliseconds}ms`;
      }

      return result.trim();
  };